import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// ─── Knowledge Base ────────────────────────────────────────────────────────────
const KB = {
  greetings: ['hi','hello','hey','good morning','good afternoon','good evening','howdy','sup'],
  farewells:  ['bye','goodbye','see you','thanks','thank you','cheers','thx'],

  faqs: [
    {
      id: 'products',
      keywords: ['product','solution','software','what do you','offer','provide','sell','service'],
      question: 'What products do you offer?',
      answer: `Softlogic Australia offers a range of innovative software solutions:\n\n• **Chefmax** — Food service & hospitality management\n• **RightIOT** — Smart IoT platform for industry\n• **NOVIQO ERP** — Enterprise resource planning\n• **Right Food Audit** — Food safety & compliance\n• **Cyber Security** — Enterprise-grade protection\n• **RPA Solutions** — Robotic process automation\n• **Facility Management** — Smart building systems`,
      links: [{ label: 'View All Products', to: '/products' }],
    },
    {
      id: 'contact',
      keywords: ['contact','phone','email','address','location','office','reach','call','get in touch'],
      question: 'How can I contact you?',
      answer: `You can reach us through:\n\n📞 **(03) 9543 7566**\n📧 **sales@softlogic.com.au**\n📍 Unit 33, 15 Ricketts Rd, Mount Waverley VIC 3149`,
      links: [{ label: 'Contact Us', to: '/contact' }],
    },
    {
      id: 'demo',
      keywords: ['demo','trial','test','try','book','schedule','appointment','presentation'],
      question: 'How do I request a demo?',
      answer: `We'd love to show you what we can do! You can request a free demo by:\n\n• Clicking **Request a Demo** in the top navigation\n• Filling out our inquiry form\n• Calling us directly on **(03) 9543 7566**\n\nOur team will get back to you within 1 business day.`,
      links: [{ label: 'Request a Demo', to: '/inquiry' }],
    },
    {
      id: 'about',
      keywords: ['about','who','company','history','experience','years','founded','team','staff'],
      question: 'Tell me about Softlogic Australia',
      answer: `Softlogic Australia is a leading IT solutions provider with **25+ years** of experience helping Australian businesses.\n\n✅ **500+** happy clients\n✅ **1000+** projects delivered\n✅ Based in **Mount Waverley, Melbourne**\n✅ Specialists in IoT, ERP, Cyber Security & more`,
      links: [{ label: 'About Us', to: '/about' }],
    },
    {
      id: 'iot',
      keywords: ['iot','internet of things','sensor','monitoring','rightiot','smart','connected','device'],
      question: 'What is RightIOT?',
      answer: `**RightIOT** is our smart IoT platform that connects and monitors sensors across your facility in real-time.\n\n🌡️ Temperature & humidity monitoring\n⚡ Energy consumption tracking\n🔒 Motion & security alerts\n🌿 Air quality & CO₂ levels\n📊 Live dashboard & reporting`,
      links: [{ label: 'Learn More', to: '/products' }],
    },
    {
      id: 'cyber',
      keywords: ['cyber','security','hack','protect','firewall','threat','vulnerability','assessment'],
      question: 'What cyber security services do you offer?',
      answer: `Our Cyber Security solutions include:\n\n🛡️ **Free Security Assessment** — identify your vulnerabilities\n🔍 **Penetration Testing** — real-world attack simulation\n🔐 **Endpoint Protection** — secure all devices\n📋 **Compliance Auditing** — meet Australian standards\n🚨 **24/7 Threat Monitoring** — always protected`,
      links: [{ label: 'Cyber Security', to: '/products' }],
    },
    {
      id: 'pricing',
      keywords: ['price','cost','pricing','how much','fee','charge','quote','affordable','cheap','expensive'],
      question: 'How much does it cost?',
      answer: `Our pricing is tailored to your business needs and scale. We offer:\n\n💼 **Custom quotes** based on your requirements\n📦 **Flexible packages** for businesses of all sizes\n🆓 **Free initial consultation** — no obligation\n\nContact us for a personalised quote!`,
      links: [{ label: 'Get a Quote', to: '/contact' }, { label: 'View Offers', to: '/offers' }],
    },
    {
      id: 'support',
      keywords: ['support','help','issue','problem','bug','maintenance','update','fix','technical'],
      question: 'Do you offer technical support?',
      answer: `Yes! We provide comprehensive support:\n\n🎯 **Dedicated support team** for all clients\n⚡ **Fast response times** — critical issues within 4 hours\n📞 **Phone & email support** during business hours\n🔧 **Ongoing maintenance** & software updates\n📚 **Training** for your team`,
      links: [{ label: 'Contact Support', to: '/contact' }],
    },
    {
      id: 'erp',
      keywords: ['erp','enterprise','resource','planning','noviqo erp','noviqo','business management','accounting','inventory'],
      question: 'What is NOVIQO ERP?',
      answer: `**NOVIQO ERP** is our comprehensive enterprise management platform that connects all your business functions:\n\n📦 Inventory & supply chain\n💰 Financial management & accounting\n👥 HR & payroll\n📊 Real-time business insights\n🔗 Integration with existing systems`,
      links: [{ label: 'Learn More', to: '/products' }],
    },
    {
      id: 'chefmax',
      keywords: ['chefmax','chef','food','restaurant','hospitality','kitchen','menu','recipe','catering'],
      question: 'What is Chefmax?',
      answer: `**Chefmax** is our award-winning food service management system designed for hospitality businesses:\n\n🍽️ Menu & recipe management\n📋 Food safety compliance\n💰 Cost & waste reduction\n📊 Nutritional analysis\n🏥 Suitable for hospitals, aged care & restaurants`,
      links: [{ label: 'Learn More', to: '/products' }],
    },
    {
      id: 'careers',
      keywords: ['job','career','work','hire','vacancy','position','apply','employment','join','team'],
      question: 'Are you hiring?',
      answer: `We're always looking for talented people to join our team! Check out our current openings on the Careers page.\n\n🚀 Innovation-driven culture\n📈 Career growth opportunities\n🌏 Work with cutting-edge technology\n🏢 Modern Melbourne office`,
      links: [{ label: 'View Careers', to: '/careers' }],
    },
    {
      id: 'rpa',
      keywords: ['rpa','automation','robotic','process','automate','workflow','bot','repetitive'],
      question: 'What is RPA?',
      answer: `**RPA (Robotic Process Automation)** lets you automate repetitive manual tasks using software bots:\n\n⚡ Up to **80% faster** processing\n🎯 **Zero errors** on repetitive tasks\n💰 Significant cost savings\n🔄 Works 24/7 without breaks\n🔗 Integrates with your existing systems`,
      links: [{ label: 'Learn More', to: '/products' }],
    },
  ],
};

// ─── Keyword matcher ─────────────────────────────────────────────────────────
function findAnswer(input) {
  const text = input.toLowerCase().trim();

  if (KB.greetings.some(g => text.includes(g))) {
    return {
      answer: `👋 Hi there! I'm the Softlogic assistant. I can help you with:\n\n• Our products & services\n• Pricing & demos\n• Contact information\n• Technical support\n\nWhat would you like to know?`,
      links: [],
      suggestions: ['What products do you offer?', 'How do I request a demo?', 'How can I contact you?'],
    };
  }

  if (KB.farewells.some(f => text.includes(f))) {
    return {
      answer: `Thanks for chatting! 😊 Feel free to come back anytime. Have a great day!`,
      links: [],
      suggestions: [],
    };
  }

  // Score each FAQ by keyword matches
  const scored = KB.faqs.map(faq => {
    const score = faq.keywords.filter(k => text.includes(k)).length;
    return { ...faq, score };
  }).filter(f => f.score > 0).sort((a, b) => b.score - a.score);

  if (scored.length > 0) {
    const best = scored[0];
    const suggestions = KB.faqs
      .filter(f => f.id !== best.id)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map(f => f.question);
    return { answer: best.answer, links: best.links || [], suggestions };
  }

  return {
    answer: `I'm not sure about that one. Here are some things I can help with:`,
    links: [],
    suggestions: ['What products do you offer?', 'How do I request a demo?', 'How can I contact you?', 'How much does it cost?'],
  };
}

// ─── Format message text ─────────────────────────────────────────────────────
function FormatText({ text }) {
  if (!text) return null;
  const parts = (text || '').split('\n').map((line, i) => {
    // Bold **text**
    const formatted = line.replace(/\*\*(.*?)\*\*/g, (_, m) => `<strong>${m}</strong>`);
    // Italic *text*
    const formatted2 = formatted.replace(/\*(.*?)\*/g, (_, m) => `<em>${m}</em>`);
    return <div key={i} style={{ minHeight: line === '' ? '0.4rem' : 'auto' }} dangerouslySetInnerHTML={{ __html: formatted2 }} />;
  });
  return <>{parts}</>;
}

// ─── Main Chatbot Component ──────────────────────────────────────────────────
export default function Chatbot() {
  const [open, setOpen]       = useState(false);
  const [messages, setMsgs]   = useState([]);
  const [input, setInput]     = useState('');
  const [typing, setTyping]   = useState(false);
  const [unread, setUnread]   = useState(0);
  const [started, setStarted] = useState(false);
  const bottomRef             = useRef(null);
  const inputRef              = useRef(null);

  // Welcome message on first open
  useEffect(() => {
    if (open && !started) {
      setStarted(true);
      setTyping(true);
      setTimeout(() => {
        setTyping(false);
        setMsgs([{
          id: 1, from: 'bot',
          text: `👋 Hi! I'm the **Softlogic** assistant.\n\nHow can I help you today?`,
          links: [],
          suggestions: ['What products do you offer?', 'How do I request a demo?', 'How can I contact you?', 'Tell me about Softlogic'],
        }]);
      }, 800);
    }
    if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 300); }
  }, [open, started]);

  // Auto scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const sendMessage = (text) => {
    if (!text.trim()) return;
    const userMsg = { id: Date.now(), from: 'user', text };
    setMsgs(prev => [...prev, userMsg]);
    setInput('');
    setTyping(true);

    setTimeout(() => {
      const result = findAnswer(text);
      setTyping(false);
      const botMsg = { id: Date.now() + 1, from: 'bot', text: result.answer, links: result.links, suggestions: result.suggestions };
      setMsgs(prev => [...prev, botMsg]);
      if (!open) setUnread(u => u + 1);
    }, 600 + Math.random() * 400);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  return (
    <>
      {/* ── Chat Window ── */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 90, right: 24, width: 360, maxHeight: 520,
          background: '#ffffff', borderRadius: 16, zIndex: 9999,
          boxShadow: '0 8px 40px rgba(22,58,107,0.18)', display: 'flex', flexDirection: 'column',
          border: '1px solid #e2e8f0', overflow: 'hidden',
          animation: 'chatSlideUp 0.25s ease',
        }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, var(--navy) 0%, var(--navy-light) 100%)', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🤖</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem', fontFamily: 'var(--font-display)' }}>Softlogic Assistant</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }}/>
                Online — typically replies instantly
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 12, background: '#f8fafd' }}>
            {messages.map(msg => (
              <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.from === 'user' ? 'flex-end' : 'flex-start', gap: 6 }}>
                <div style={{
                  maxWidth: '85%', padding: '10px 14px', borderRadius: msg.from === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.from === 'user' ? 'var(--navy)' : '#fff',
                  color: msg.from === 'user' ? '#fff' : '#1e293b',
                  fontSize: '0.86rem', lineHeight: 1.6, fontFamily: 'var(--font-sans)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  border: msg.from === 'bot' ? '1px solid var(--border)' : 'none',
                }}>
                  <FormatText text={msg.text} />
                </div>
                {/* Links */}
                {msg.links?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: '85%' }}>
                    {msg.links.map((l, i) => (
                      <Link key={i} to={l.to} onClick={() => setOpen(false)} style={{
                        display: 'inline-block', padding: '5px 12px', borderRadius: 20,
                        background: '#eff6ff', border: '1px solid rgba(0,102,255,0.2)',
                        color: '#0066ff', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none',
                      }}>{l.label} →</Link>
                    ))}
                  </div>
                )}
                {/* Suggestions */}
                {msg.from === 'bot' && msg.suggestions?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxWidth: '90%', width: '100%' }}>
                    {msg.suggestions.map((s, i) => (
                      <button key={i} onClick={() => sendMessage(s)} style={{
                        textAlign: 'left', padding: '7px 12px', borderRadius: 8,
                        background: '#fff', border: '1px solid #e2e8f0',
                        color: '#334155', fontSize: '0.8rem', cursor: 'pointer',
                        transition: 'all 0.15s', fontFamily: 'var(--font-sans)',
                      }}
                      onMouseEnter={e => { e.target.style.borderColor = '#0066ff'; e.target.style.color = '#0066ff'; }}
                      onMouseLeave={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.color = '#334155'; }}>
                        💬 {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {typing && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px 16px 16px 4px', padding: '12px 16px', display: 'flex', gap: 4, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--sky-dark)', animation: `chatDot 1.2s ease-in-out ${i * 0.2}s infinite` }}/>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '12px 14px', borderTop: '1px solid #e2e8f0', background: '#fff', display: 'flex', gap: 8 }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask me anything..."
              style={{
                flex: 1, padding: '9px 14px', borderRadius: 24, border: '1.5px solid var(--border)',
                fontSize: '0.86rem', fontFamily: 'var(--font-sans)', outline: 'none',
                background: '#f8fafd', color: '#1e293b',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--sky-dark)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim()}
              style={{
                width: 38, height: 38, borderRadius: '50%', border: 'none',
                background: input.trim() ? 'linear-gradient(135deg,#0066ff,#00d4ff)' : '#e2e8f0',
                color: '#fff', cursor: input.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1rem', transition: 'background 0.2s', flexShrink: 0,
              }}>
              ➤
            </button>
          </div>

          {/* Footer */}
          <div style={{ padding: '6px 14px 10px', background: '#fff', textAlign: 'center' }}>
            <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Powered by Softlogic Australia</span>
          </div>
        </div>
      )}

      {/* ── Bubble Button ── */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: 24, right: 24, width: 58, height: 58,
          borderRadius: '50%', border: 'none', zIndex: 9999, cursor: 'pointer',
          background: 'linear-gradient(135deg, var(--navy) 0%, var(--sky-dark) 100%)',
          boxShadow: '0 4px 20px rgba(22,58,107,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: open ? '1.4rem' : '1.6rem',
          transition: 'all 0.3s',
          transform: open ? 'rotate(0deg)' : 'rotate(0deg)',
        }}>
        {open ? '✕' : '💬'}
        {!open && unread > 0 && (
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 20, height: 20,
            borderRadius: '50%', background: '#ef4444', color: '#fff',
            fontSize: '0.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fff',
          }}>{unread}</div>
        )}
      </button>

      {/* ── Animations ── */}
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes chatDot {
          0%,80%,100% { transform: scale(0.7); opacity: 0.4; }
          40%          { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </>
  );
}
